import React from 'react';
import {DoppeActionTypeCoupon, doppeActionTypeCouponResolveSettingsFromBoundEntity} from '../doppe-action-type-coupon';
import {ButtonType} from '@wix/devzai-utils-react';
import {classes, style} from './doppe-action-type-coupon-widget.st.css';
import {
    DoppeActionTypeWidgetHeader
} from '../../../components/doppe-action-type-widget-header/doppe-action-type-widget-header';
import {
    CouponSourceType,
    DoppeActionTypeCouponView
} from '../../../components/doppe-action-type-coupon-view/doppe-action-type-coupon-view';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetAutoSizeImageView
} from '../../../components/doppe-action-type-widget-auto-size-image-view/doppe-action-type-widget-auto-size-image-view';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {doppeHideableValueIsHidden} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeWidgetContentLayoutNew
} from '../../../components/doppe-action-type-widget-content-layout-new/doppe-action-type-widget-content-layout-new';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalCouponProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    useDoppeViewerLangDictionary
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';

export const DoppeActionTypeCouponWidget = React.memo(function DoppeActionTypeCouponWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeCoupon.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const action = actionContext.action;

    const actionSettings = action.settings;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            <DoppeActionTypeWidgetHeader
                actionContext={actionContext}
                withBorderBottom={false}
            />
            <DoppeActionTypeCouponBindableWidget
                actionId={action.id}
                reportGoalAchieved={() => {
                    actionContext.reportGoalAchieved()
                }}
                actionSettings={actionSettings}
            />
        </div>
    );
});


export const DoppeActionTypeCouponBindableWidget = React.memo(function DoppeActionTypeCouponBindableWidget(
    props: {
        actionSettings: DoppeActionTypeCoupon.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();

    const {
        couponProvider,
        couponProviderId
    } = actionSettings;


    if (couponProvider !== null && couponProviderId !== null) {
        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalCouponProviderResolveBusinessEntityReference(couponProvider, couponProviderId))

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) {
            return (
                <DoppeActionTypeCouponWidgetContentUnavailable
                    reason={
                        !boundService ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            const resolvedActionSettings = doppeActionTypeCouponResolveSettingsFromBoundEntity(actionSettings, boundService);

            return (
                <DoppeActionTypeCouponWidgetInner
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={resolvedActionSettings}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeCouponWidgetInner
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
            />
        )
    }
});

export const DoppeActionTypeCouponWidgetContentUnavailable = React.memo(function DoppeActionTypeServiceWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLang = useDoppeViewerLangDictionary();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLang.contentUnavailableTitleService}
        />
    )
});

export const DoppeActionTypeCouponWidgetInner = React.memo(function DoppeActionTypeCouponWidgetInner(
    props: {
        actionSettings: DoppeActionTypeCoupon.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
    } = props;

    const {
        coupon,
        description,
        title,
        couponPageLink,
        showCouponButtonLink,
        couponButtonText,
        couponImage
    } = actionSettings;

    return (
        <DoppeActionTypeWidgetContentLayoutNew
            mainMedia={couponImage ? (
                <DoppeActionTypeWidgetAutoSizeImageView
                    imageResource={couponImage}
                />
            ) : null}
            footer={(
                showCouponButtonLink ?
                    <DoppeActionTypeWidgetInnerButton
                        buttonType={ButtonType.NormalButton}
                        tagName={'a'}
                        href={couponPageLink}
                        target={'_blank'}
                        onClick={() => {
                            reportGoalAchieved();
                        }}
                    >
                        {couponButtonText}
                    </DoppeActionTypeWidgetInnerButton>
                    :
                    null
            )}
        >
            <DoppeActionTypeWidgetInnerTitle
                className={style(classes.couponName, {})}
            >
                {title}
            </DoppeActionTypeWidgetInnerTitle>

            <DoppeActionTypeWidgetInnerDescription
                className={style(classes.couponDescription, {})}
            >
                {description}
            </DoppeActionTypeWidgetInnerDescription>
            {
                !doppeHideableValueIsHidden(coupon) ?
                    <DoppeActionTypeCouponView
                        className={style(classes.couponView, {})}
                        coupon={coupon}
                        couponSourceType={CouponSourceType.Action}
                        actionId={actionId}
                    />
                    :
                    null
            }
        </DoppeActionTypeWidgetContentLayoutNew>
    );
});
